import { ProjectStatus as StatusType } from '../types';
import { CheckCircle, Clock, Settings, Rocket, Eye, AlertTriangle, Upload, Hourglass, Globe, RefreshCw, PauseCircle, Wrench, Store, TestTube, ShieldCheck, XCircle } from 'lucide-react';

interface ProjectStatusProps {
  status: StatusType | string;
  className?: string;
}

const statusConfig: Record<
  string,
  {
    label: string;
    description: string;
    icon: typeof Clock;
    color: string;
    bgColor: string;
    borderColor: string;
  }
> = {
  stage1_awaiting_customer: {
    label: 'Awaiting Customer Input',
    description: 'Waiting for customer to complete the setup form',
    icon: Clock,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
  },
  stage2_provisioning: {
    label: 'Provisioning',
    description: 'Setting up your application infrastructure',
    icon: Settings,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  stage3_complete: {
    label: 'Setup Complete',
    description: 'Your application has been configured successfully',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
  },
  stage3_development: {
    label: 'Building App Config',
    description: 'We are actively building and configuring your application',
    icon: Wrench,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  stage4_deployed: {
    label: 'Deployed',
    description: 'Your app has passed App Store review and is live / published',
    icon: Rocket,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
  stage4_app_store_registration: {
    label: 'App Store Registration',
    description: 'Registering your application in the App Store',
    icon: Store,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
  },
  stage5_testing: {
    label: 'Testing',
    description: 'Providing testing links and TestFlight access',
    icon: TestTube,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
  },
  stage6_internal_approval: {
    label: 'Internal Approval',
    description: 'Your application is awaiting internal approval',
    icon: ShieldCheck,
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
  },
  customer_review: {
    label: 'Customer Review',
    description: 'Please review and approve your application for App Store submission',
    icon: Eye,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
  },
  resolve_issues: {
    label: 'Resolving Issues',
    description: 'Your change request was received and our team is working on it',
    icon: Wrench,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
  },
  waiting_for_model_file: {
    label: 'Waiting for Model File',
    description: 'Action required: complete model/inventory details so we can continue',
    icon: AlertTriangle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  inventory_clean_up_required: {
    label: 'Inventory Clean Up Required',
    description: 'Action required: clean up shop models (rate, term, price, image, Show on Web)',
    icon: AlertTriangle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  apple_agreement_update_needed: {
    label: 'Apple Agreement Update Needed',
    description: 'Action required: Please accept the latest Apple Developer Agreement update',
    icon: AlertTriangle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
  },
  submit_to_app_store: {
    label: 'Submitting to App Store',
    description: 'We are currently preparing and submitting your app to the App Store',
    icon: Upload,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
  },
  waiting_on_app_store_approval: {
    label: 'Waiting on App Store Approval',
    description: 'Your app has been submitted and is under review by Apple and/or Google',
    icon: Hourglass,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  deployment_finished: {
    label: 'Deployment Finished',
    description: '🎉 Your application is now live and available for download on the App Stores!',
    icon: Globe,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
  },
  stage_for_update: {
    label: 'Stage for Update',
    description: 'A new version of your application is ready - we are transitioning you to the latest version',
    icon: RefreshCw,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
  },
  on_hold: {
    label: 'On Hold',
    description: 'This project is currently paused',
    icon: PauseCircle,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
  },
  cancelled: {
    label: 'Cancelled',
    description: 'This project has been cancelled',
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
  },
};

const fallbackConfig = {
  label: 'Project Status',
  description: 'Your project status is being updated',
  icon: Clock,
  color: 'text-gray-600',
  bgColor: 'bg-gray-50',
  borderColor: 'border-gray-200',
};

export function ProjectStatus({ status, className = '' }: ProjectStatusProps) {
  const config = statusConfig[status] ?? {
    ...fallbackConfig,
    label: status ? String(status).replace(/_/g, ' ') : fallbackConfig.label,
    description: status
      ? `Current status: ${String(status).replace(/_/g, ' ')}`
      : fallbackConfig.description,
  };
  const Icon = config.icon;

  return (
    <div className={`rounded-lg border p-4 mb-8 ${config.bgColor} ${config.borderColor} ${className}`}>
      <div className="flex items-center space-x-3">
        <Icon className={`h-6 w-6 ${config.color}`} />
        <div>
          <h3 className={`text-sm font-medium ${config.color}`}>{config.label}</h3>
          <p className="text-sm text-gray-600">{config.description}</p>
        </div>
      </div>
    </div>
  );
}
